import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, PageLevelAlert, Loader, SelectField, SelectItem } from "@kuda/ui";
import { CodeIcon, GridIcon } from "@kuda/ui/icons";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading";
import Dialogbox from "../components/dialogs/dialogBox";
import Breadcrumbs from "../components/breadcrumbs";
import CodeEditor from "../components/monacoEditor";
import AwsProfileSelector from "../components/AwsProfileSelector";
import { useSecrets } from "../components/hooks/useSecrets";
import { useApiHeaders } from "../components/hooks/useApiHeaders";
import {
  getTagValue,
  setTagValue,
  updateTagsWithUser,
  validateJson,
} from "../components/utils/tagHelpers";
import { config } from "../components/config";
import useAccessToken from "../auth/accessPermission";
import useUserAccount from "../components/hooks/userAccountInfo";

function UpdateSecretForm() {
  const [secret, setSecret] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAppsettings, setIsAppsettings] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [tags, setTags] = useState([]);
  const [openError, setOpenError] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Environment/Team are editable independently of the secret's value —
  // initialEnvironment/initialTeam track the loaded values so we know whether
  // they've actually changed (and can save a tags-only edit with no code change).
  const [environment, setEnvironment] = useState("");
  const [team, setTeam] = useState("");
  const [initialEnvironment, setInitialEnvironment] = useState("");
  const [initialTeam, setInitialTeam] = useState("");

  const navigate = useNavigate();
  const { instance } = useMsal();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const platformName = searchParams.get("platformName");
  const selectedCluster = searchParams.get("selectedCluster");
  const urlVaultId = searchParams.get("vaultName");
  const urlAwsProfile = searchParams.get("awsProfile");
  // "*" not "secretName" — the route is a splat (/update-secret/*) so nested Vault
  // paths like dev/kuda-internaltools/foo survive intact instead of being cut at
  // the first slash.
  const { "*": secretName } = useParams();

  // AWS Profile state - ONLY relevant for AWS platform
  const [awsProfile, setAwsProfile] = useState(() => {
    // Only initialize for AWS platform
    if (platformName === config.platforms[0] && urlAwsProfile && config.awsProfiles[urlAwsProfile]) {
      return urlAwsProfile;
    }
    return 'profile-a';
  });

  // VaultId state - conditional initialization based on platform
  const [currentVaultId, setCurrentVaultId] = useState(() => {
    // For AWS: use profile's region
    if (platformName === config.platforms[0] && urlAwsProfile && config.awsProfiles[urlAwsProfile]) {
      return config.awsProfiles[urlAwsProfile].region;
    }
    // For other platforms (Azure KV, Vault): use URL parameter as-is
    return urlVaultId;
  });

  const { roles, nameParts } = useUserAccount(instance);
  const accessToken = useAccessToken([config.apiAccess]);
  const { loading, fetchSecret, updateSecret } = useSecrets();

  const headers = useApiHeaders(
    accessToken,
    roles,
    platformName,
    currentVaultId,
    awsProfile  // Pass AWS profile (only used for AWS platform)
  );

  // headersRef: always current headers without causing re-renders
  const headersRef = useRef(headers);
  useEffect(() => { headersRef.current = headers; }, [headers]);

  // Update vaultId when AWS profile changes - ONLY for AWS platform
  useEffect(() => {
    if (platformName === config.platforms[0] && config.awsProfiles[awsProfile]) {
      const profileRegion = config.awsProfiles[awsProfile].region;
      setCurrentVaultId(profileRegion);
    }
    // For other platforms, don't change vaultId
  }, [platformName, awsProfile]);

  // AWS profile change handler - ONLY for AWS platform
  const handleAwsProfileChange = useCallback((profile) => {
    setAwsProfile(profile);

    // Update vault ID for AWS
    if (platformName === config.platforms[0]) {
      const profileConfig = config.awsProfiles[profile];
      if (profileConfig) {
        setCurrentVaultId(profileConfig.region);
      }
    }

    // Update URL
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("awsProfile", profile);
    newSearchParams.set("vaultName", config.awsProfiles[profile].region);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  }, [platformName, location, navigate]);

  const loadSecret = useCallback(async () => {
    if (roles.length > 0 && headersRef.current && accessToken) {
      try {
        const response = await fetchSecret(secretName, platformName, headersRef.current);
        const secretData = response.secretValue;
        const tagData = response.tags || [];
        setTags(tagData);

        const env = getTagValue(tagData, "Environment");
        const proj = getTagValue(tagData, "Project");
        setEnvironment(env);
        setTeam(proj);
        setInitialEnvironment(env);
        setInitialTeam(proj);

        if (Object.keys(secretData)[0] === "appsettings.json") {
          setIsAppsettings(true);
          setInitialValue(
            JSON.stringify(secretData["appsettings.json"], null, 2)
          );
        } else {
          setInitialValue(JSON.stringify(secretData, null, 2));
        }
      } catch (error) {
        console.error("Error fetching secret:", error);
        const errorMsg = error.response?.data?.error || "Failed to load secret";
        setErrorMessage(errorMsg);
        setOpenError(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secretName, platformName, accessToken, fetchSecret, currentVaultId, awsProfile]);

  useEffect(() => {
    loadSecret();
  }, [loadSecret]);

  const handleSuccessAlertClose = useCallback(() => {
    setOpenSuccess(false);

    // Build navigation URL with proper parameters
    const params = new URLSearchParams({
      platformName,
      selectedCluster,
      vaultName: currentVaultId
    });

    // Add AWS profile for AWS platform
    if (platformName === config.platforms[0] && awsProfile) {
      params.set('awsProfile', awsProfile);
    }

    // AWS/Vault need a moment to propagate a write before it's reliably
    // readable again — without this, landing straight back on the view page
    // can briefly show the pre-update value/tags until a later refetch.
    // Reuses the same full-screen loader shown during the update itself.
    setUpdating(true);
    setTimeout(() => {
      setUpdating(false);
      navigate(`/view-secret/${secretName}?${params.toString()}`);
    }, 1500);
  }, [navigate, secretName, platformName, selectedCluster, currentVaultId, awsProfile]);

  const handleCodeMirrorChange = useCallback(
    (value) => {
      setSecret(value);
      setIsModified(
        value.replace(/\s/g, "") !== initialValue.replace(/\s/g, "")
      );
    },
    [initialValue]
  );

  const tagsModified = environment !== initialEnvironment || team !== initialTeam;

  const handleUpdateSecret = useCallback(async () => {
    const contentChanged = secret && isModified;

    if (!contentChanged && !tagsModified) {
      setWarningMessage("No changes were made!");
      setOpenWarning(true);
      return;
    }

    // Content unchanged but tags were — fall back to the value already loaded.
    const valueToSave = contentChanged ? secret : initialValue;

    if (!validateJson(valueToSave)) {
      setErrorMessage("Syntax error please check your input");
      setOpenError(true);
      return;
    }

    try {
      setUpdating(true);
      const updatedSecret = isAppsettings
        ? `{"appsettings.json": ${valueToSave}}`
        : valueToSave;

      let newTags = updateTagsWithUser(tags, nameParts, platformName);
      newTags = setTagValue(newTags, "Environment", environment, platformName);
      newTags = setTagValue(newTags, "Project", team, platformName);

      const payload = {
        secretValue: updatedSecret,
        tags: newTags,
      };

      const response = await updateSecret(
        secretName,
        payload,
        platformName,
        headersRef.current
      );
      if (response.status === 200) {
        setOpenSuccess(true);
      } else {
        setErrorMessage("Error updating secret");
        setOpenError(true);
      }
    } catch (error) {
      const backendError = error.response?.data?.error || "Unknown error";
      setErrorMessage(backendError);
      setOpenError(true);
    } finally {
      setUpdating(false);
    }
  }, [
    secret,
    isModified,
    tagsModified,
    initialValue,
    isAppsettings,
    tags,
    environment,
    team,
    nameParts,
    platformName,
    secretName,
    updateSecret,
    // headers/roles omitted — headersRef.current used inside
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const handleBCWarningAlertClose = useCallback(() => {
    setOpenWarning(false);

    // Build navigation URL with proper parameters
    const params = new URLSearchParams({
      platformName,
      selectedCluster,
      vaultName: currentVaultId
    });

    // Add AWS profile for AWS platform
    if (platformName === config.platforms[0] && awsProfile) {
      params.set('awsProfile', awsProfile);
    }

    navigate(`/view-secret/${secretName}?${params.toString()}`);
  }, [navigate, secretName, platformName, selectedCluster, currentVaultId, awsProfile]);

  const handleOpenWarning = useCallback(() => {
    setOpenWarning(true);
    setWarningMessage("Changes not saved!");
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenError(false);
    setOpenWarning(false);
    setOpenSuccess(false);
  }, []);

  return (
    <div>
      <div className="pl-8 mb-4">
        <Breadcrumbs onIsModified={handleOpenWarning} isChange={isModified || tagsModified} />
      </div>

      {/* Skeleton loader only for initial fetch */}
      {loading && !initialValue ? (
        <div className="flex justify-center">
          <Loading
            open={loading}
            type="skeleton"
            variant="code"
            rows={32}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center" style={{ minHeight: "100vh" }}>
          <div className="flex justify-between items-center mb-4" style={{ width: '60rem' }}>
            <h1 className="font-heading text-3xl font-bold text-color-text-brand-primary-default" style={{ marginTop: 20 }}>
              Update Secret
            </h1>

            {/* AWS Profile Selector - ONLY shown for AWS platform */}
            <AwsProfileSelector
              selectedProfile={awsProfile}
              onProfileChange={handleAwsProfileChange}
              platformName={platformName}
            />
          </div>

          {/* Current profile info for AWS - ONLY shown for AWS platform */}
          {platformName === config.platforms[0] && config.awsProfiles[awsProfile] && (
            <div className="mb-4" style={{ width: '60rem' }}>
              <PageLevelAlert type="info" width="100%">
                <strong>AWS Profile:</strong> {config.awsProfiles[awsProfile].name} |
                Region: {config.awsProfiles[awsProfile].region} |
                Account: {config.awsProfiles[awsProfile].account}
              </PageLevelAlert>
            </div>
          )}

          {/* Environment/Team can be edited in place — no need to duplicate the secret */}
          <div className="grid grid-cols-2 gap-4 mb-4" style={{ width: "60rem" }}>
            <SelectField label="Environment" placeholder="Select environment" value={environment} onValueChange={setEnvironment}>
              {config.environments.map((env) => (
                <SelectItem key={env.value} value={env.value}>{env.label}</SelectItem>
              ))}
            </SelectField>
            <SelectField label="Team" placeholder="Select team" value={team} onValueChange={setTeam}>
              {config.projects.map((proj) => (
                <SelectItem key={proj.value} value={proj.value}>{proj.label}</SelectItem>
              ))}
            </SelectField>
          </div>

          <div style={{ width: "60rem" }}>
            <div className="w-full">
              <Dialogbox
                open={openSuccess}
                onClose={handleSuccessAlertClose}
                title="Success"
                severity="success"
                message={
                  <div>
                    {secretName} updated successfully! <br />
                    Click on the close button to view secret
                  </div>
                }
              />

              <Dialogbox
                open={openWarning}
                onClose={handleDialogClose}
                title="Warning"
                severity="warning"
                message={
                  <div>
                    {warningMessage} <br />
                    Click on the button to continue editing or discard
                  </div>
                }
                onConfirm={handleBCWarningAlertClose}
                confirmText="Discard"
                closeText="Back to editing"
              />

              <Dialogbox
                open={openError}
                onClose={handleDialogClose}
                title="Error"
                severity="error"
                message={
                  <div>
                    Failed to update! <br />
                    Message: {errorMessage}
                  </div>
                }
              />
            </div>

            <div className="flex justify-between items-center">
              <p className="flex items-center gap-2 text-color-text-state-warning-default" style={{ marginBottom: 3, fontSize: 20 }}>
                <CodeIcon className="size-5" />
                {secretName}
              </p>
              <p className="flex items-center gap-2 text-color-text-state-warning-default" style={{ marginBottom: 3, fontSize: 20 }}>
                <GridIcon className="size-5" />
                {selectedCluster} Cluster
              </p>
            </div>

            <div className="flex flex-col justify-between rounded-lg bg-color-bg-surface border border-color-border-secondary m-1" style={{ width: "60rem", height: "auto" }}>
              <div className="flex justify-center">
                <div className="my-2 p-1 bg-color-bg-secondary border border-color-border-secondary rounded-lg flex justify-center" style={{ width: "55rem", height: "40rem" }}>
                  <div style={{ overflow: "auto" }}>
                    <CodeEditor
                      language="json"
                      value={initialValue}
                      onChange={handleCodeMirrorChange}
                      readOnly="false"
                      showMinimap="true"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-center bg-color-bg-secondary" style={{ height: "2.5rem" }}>
                <Button variant="text" className="px-3" onClick={handleUpdateSecret}>
                  Update
                </Button>
                <Button variant="outlined" className="px-3 ml-2 mr-5" onClick={handleOpenWarning}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spinner overlay only when updating */}
      {updating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Loader type="brand" />
        </div>
      )}
    </div>
  );
}

export default UpdateSecretForm;
